import React, { useEffect, useRef, useState } from 'react'

const CELL_CLASSES = [
  'h-[56px]',
  'w-[44px]',
  'sm:w-[48px]',
  'rounded-[12px]',
  'border',
  'border-ink-100/15',
  'bg-white-500',
  'text-center',
  'font-display',
  'text-[24px]',
  'font-medium',
  'text-ink-700',
  'tabular-nums',
  'outline-none',
  'transition-all',
  'duration-200',
  'placeholder:text-ink-100/40',
  'focus:border-blue-500',
  'focus:shadow-[0_0_0_3px_#D8F1FF]',
  'hover:border-ink-100/40'
].join(' ')

const CodeInput: React.FC<{
  onComplete: (code: string) => void
}> = ({ onComplete }) => {
  const [codes, setCodes] = useState<string[]>(['', '', '', '', '', ''])

  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(6).fill(null))

  // `onComplete` is read through a ref so the submit effect below depends ONLY
  // on the digits. Every consumer passes an inline arrow or a plain function
  // declared in the parent's body, so the prop gets a fresh identity on every
  // parent render — an identity-dependent effect therefore re-fires on the
  // parent's reaction to its OWN result.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  })

  // The completed code already handed to the consumer. Guarantees exactly one
  // submit per distinct code, however often this component re-renders.
  //
  // 2026-07-31 incident: one expired OTP on /reservar put 87 `confirm()` calls
  // on the wire in 14 seconds. `handleOtpComplete` set `formError` on failure →
  // parent re-rendered → new `onComplete` identity → both of the old effects
  // re-ran → the same code was submitted again → failed again. Firebase
  // answered with its undocumented `-39` abuse throttle, after which the user
  // could not verify at all. Five sessions hit this in four days; every one
  // started from a single ordinary `auth/code-expired`.
  //
  // The old second effect (`[isCodeComplete, onComplete]`) also called
  // `onComplete` with PARTIAL codes and on mount. Nothing depends on that —
  // both consumers gate on `length === 6` — so it is gone rather than fixed.
  const lastSubmittedRef = useRef<string | null>(null)

  useEffect(() => {
    const code = codes.join('')
    if (code.length !== 6) {
      // Editing any digit re-arms submission, so a corrected code still fires.
      lastSubmittedRef.current = null
      return
    }
    if (lastSubmittedRef.current === code) return
    lastSubmittedRef.current = code
    onCompleteRef.current(code)
  }, [codes])

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newCode = event.target.value.replace(/[^0-9]/g, '').slice(0, 1)
    const newCodes = [...codes]
    newCodes[index] = newCode
    setCodes(newCodes)

    if (newCode && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === 'Backspace' && !codes[index] && index > 0) {
      event.preventDefault()
      const newCodes = [...codes]
      newCodes[index - 1] = ''
      setCodes(newCodes)
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, 6)
    if (!pasted) return
    event.preventDefault()
    const next = pasted.split('').concat(Array(6).fill('')).slice(0, 6)
    setCodes(next)
    const focusIndex = Math.min(pasted.length, 5)
    inputRefs.current[focusIndex]?.focus()
  }

  return (
    <div className="flex w-full items-center justify-between gap-2 sm:justify-start sm:gap-3">
      {Array.from({ length: 6 }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={codes[index] || ''}
          onChange={(e) => handleInputChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={index === 0 ? handlePaste : undefined}
          autoFocus={index === 0}
          className={CELL_CLASSES}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  )
}

export default CodeInput
